import React from 'react';
import cn from '../utils/cn';

type KoobStatProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> & {
  /** Small uppercase kicker (e.g. "Current trip:", "From", "Hotel total price"). */
  label: React.ReactNode;
  /** Main value (trip name, formatted price…). */
  value: React.ReactNode;
  /** Secondary line under the value (e.g. per-pax price). */
  subValue?: React.ReactNode;
  /** 'column' stacks the label above the value; 'row' puts it beside. */
  layout?: 'column' | 'row';
  className?: string;
  valueClassName?: string;
  /** Extra props for the value wrapper (e.g. a test id). */
  valueProps?: React.HTMLAttributes<HTMLDivElement>;
};

/**
 * KD-3391 trip designer typography atom: 9px bold uppercase kicker + 14px
 * brand-colored value + optional 12px muted sub-line. Used by the trip
 * designer navbar strip and the compose column total-price cards.
 */
export default function KoobStat({
  label,
  value,
  subValue,
  layout = 'column',
  className,
  valueClassName,
  valueProps,
  ...props
}: KoobStatProps) {
  return (
    <div
      className={cn(
        'min-w-0',
        layout === 'row'
          ? 'flex items-center gap-3'
          : 'flex flex-col justify-center',
        className,
      )}
      {...props}
    >
      <div className="text-[9px] font-bold uppercase tracking-widest leading-tight text-muted-foreground">
        {label}
      </div>

      <div className="leading-tight" {...valueProps}>
        <div className={cn('text-sm font-medium text-brand', valueClassName)}>
          {value}
        </div>

        {subValue && (
          <div className="text-xs text-muted-foreground">{subValue}</div>
        )}
      </div>
    </div>
  );
}
